<script setup>
import {computed, ref, watch} from "vue"
import {useForm} from "@inertiajs/vue3"
import JsonEditorVue from 'json-editor-vue'
import FormButton from "@/components/Shared/Form/Button.vue"
import FormInput from "@/components/Shared/Form/Input/Index.vue"
import Modal from "@/components/Shared/Modal.vue"
import FormInputLabel from "@/components/Shared/Form/Input/Label.vue";
import FormInputError from "@/components/Shared/Form/Input/Error.vue";

const emit = defineEmits(['close', 'update'])

const props = defineProps({
  show: Boolean,
  userIntegrationId: Number,
  userIntegrationFlowId: Number,
  selectedEndpoint: {
    type: Object,
    default: {
      user_application_id: null,
      name: '',
      endpoint: '',
      method: '',
    }
  }
})

const defaultFormData = {
  user_application_id: null,
  endpoint: null,
  method: null,
  body: '',
  parameters: [],
}

const form = useForm(defaultFormData)

const response = ref('')

const endpointParameters = computed(() => Array.from((form.endpoint ?? '').matchAll(/{(\w+\b)(?!.*\b\1\b)}/g), x => x[1]))

const testEndpoint = () => {
  form.processing = true
  form.clearErrors()

  response.value = ''

  axios
    .post(`/api/integrations/${props.userIntegrationId}/flows/${props.userIntegrationFlowId}/endpoints/test`, form)
    .then((result) => response.value = result.data)
    .catch(function (error) {
      if (error.response.status === 422) {
        form.setError(error.response.data.errors)
      } else {
        response.value = error.response.data

        throw error
      }
    })
    .finally(() => form.processing = false)
}

const close = () => emit('close')

watch(() => props.show, value => {
  const defaults = value ? props.selectedEndpoint : defaultFormData

  response.value = ''

  form.defaults(defaults)
  form.reset()
})

watch(endpointParameters, function (parameters) {
  form.parameters = parameters.map(parameter => ({
    name: parameter,
    value: form.parameters.find((formParameter) => formParameter.name === parameter)?.value ?? ''
  }))
})
</script>

<template>
  <modal class="test-endpoint-modal" v-if="selectedEndpoint" :show="show" @close="close">
    <template #title>
      Test endpoint: {{ selectedEndpoint.name }} in {{ selectedEndpoint.applicationName }}
    </template>
    <template #content>
      <div class="mt-4">
        <form-input
          v-model="form.endpoint"
          type="input"
          label-text="Endpoint"
          :error="form.errors.endpoint"
          required
        />
      </div>
      <div class="mt-4">
        <form-input
          v-model="form.method"
          type="select"
          :options="['GET', 'POST', 'PUT', 'PATCH', 'MERGE', 'DELETE']"
          label-text="Method"
          :error="form.errors.method"
          :placeholder="trans('dashboard.integrations.flows.select_an_option')"
          :allow-empty="false"
          required
        />
      </div>
      <div v-for="(parameter, index) in form.parameters" class="mt-4">
        <form-input
          v-model="parameter.value"
          type="input"
          :label-text="`Parameter: ${parameter.name}`"
          :error="form.errors[`parameters.${index}.value`]"
          required
        />
      </div>

      <div v-if="['POST', 'PUT', 'PATCH', 'MERGE'].includes(form.method)" class="mt-4">
        <form-input-label value="Body" required/>
        <json-editor-vue v-model="form.body" mode="text"/>
        <input :class="{ 'is-invalid': form.errors.body }" class="form-control" type="hidden"/>
        <form-input-error v-if="form.errors.body" :error-message="form.errors.body"/>
      </div>

      <div v-if="response" class="mt-4">
        <form-input-label value="Response"/>
        <json-editor-vue v-model="response" mode="text" :read-only="true"/>
      </div>
    </template>
    <template #footer>
      <form-button class="button_cancel" :value="trans('common.cancel')" @click="close"/>

      <form-button class="ml-3" :value="trans('common.test')" :processing="form.processing" @click="testEndpoint"/>
    </template>
  </modal>
</template>

<style>
.test-endpoint-modal .modal-content {
  min-width: 50%;
  overflow-y: auto;
}
</style>
